<template>
  <RouterView />
  <!-- Blockierende Ebene für ausstehende Datenschutz-Zustimmungen. Bewusst hier
       und nicht auf einer Route: die Sperre muss jeden Einstieg abdecken. -->
  <ConsentGate />
</template>

<script lang="ts">
import { defineComponent, nextTick, onBeforeMount, onMounted, onUnmounted, watch } from "vue";
import { RouterView } from "vue-router";
import ConsentGate from "@/components/ConsentGate.vue";
import { useUserStore } from "@/stores/user";
import { useConfigStore } from "@/stores/config";
import { useBodyStore } from "@/stores/body";
import { useAuthStore } from "@/stores/auth";
import { useTempStore } from "@/stores/temp";
import { useTablesStore } from "@/stores/tables";
import { initializeComponents } from "@/core/plugins/keenthemes";
import { useCommunicationStore } from "@/stores/communication";
import { serverWorker } from "@/core/worker/workerInstances";
import { hideNativeSplash } from "@/core/native";
import { useRegisterSW } from "virtual:pwa-register/vue";
// import WorkerService from "@/core/services/WorkerService";

export default defineComponent({
  name: "app",
  components: {
    RouterView,
    ConsentGate,
  },
  setup() {
    const userStore = useUserStore();
    const configStore = useConfigStore();
    const bodyStore = useBodyStore();
    const authStore = useAuthStore();
    const tempStore = useTempStore();
    const tablesStore = useTablesStore()
    const communicationStore = useCommunicationStore()

    // PWA: Prüft alle 60 Sekunden ob ein neues SW-Update verfügbar ist.
    // Besonders wichtig für die iPhone Home-Screen-App (Safari cached sehr aggressiv).
    // Mit registerType: 'autoUpdate' in vite.config.ts lädt die App nach dem
    // SW-Update automatisch neu (skipWaiting + clients.claim).
    useRegisterSW({
      onRegisteredSW(_swUrl, registration) {
        if (registration) {
          setInterval(() => {
            registration.update();
          }, 60_000); // alle 60 Sekunden prüfen
        }
      },
    });

    onBeforeMount(() => {
      /**
       * Overrides the layout config using saved data from localStorage
       * remove this to use static config (@/core/config/DefaultLayoutConfig.ts)
       */
      userStore.checkactivesettings()
      configStore.overrideLayoutConfig();
    });

    onMounted(() => {
      nextTick(() => {
        initializeComponents();
        bodyStore.removeBodyClassName("page-loading");
        // Erst jetzt steht wirklich etwas auf dem Schirm — der native Splash
        // haelt bis hierher (launchAutoHide: false in capacitor.config.ts),
        // sonst klafft dazwischen ein weisser Frame. No-op im Browser.
        hideNativeSplash();
      });

      // iOS friert Seite samt Workern im Hintergrund ein und kappt den Socket;
      // der Worker selbst kann die Sichtbarkeit des Dokuments nicht beobachten.
      // Ohne diese Listener blieben die 'visibility'/'network'-Fälle im
      // server.worker unerreichbar und die App wartete nach dem Aufwachen auf
      // den Heartbeat-Timeout bzw. den nächsten Reconnect, bevor Daten kamen.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          serverWorker.postMessage({ cmd: "visibility", task: "visible" });
        }
      });
      window.addEventListener("online", () => {
        serverWorker.postMessage({ cmd: "network", task: "online" });
      });
      // bfcache-Restore (Safari) feuert kein visibilitychange, nur pageshow.
      window.addEventListener("pageshow", (e: PageTransitionEvent) => {
        if (e.persisted) {
          serverWorker.postMessage({ cmd: "visibility", task: "visible" });
        }
      });

      // Start communication polling as soon as the user is authenticated AND
      // the active company is known.
      //
      // Die Company MUSS mitbeobachtet werden: `isAuthenticated` wird in
      // setAuth() gesetzt, `ActiveCompany` erst danach asynchron von
      // bootstrapActiveCompany(). Ein Watcher nur auf isAuthenticated feuerte
      // deshalb bei jedem Reload mit leerer Company, tat nichts — und feuerte
      // nie wieder. Genau daran starteten Gmail-Poll und Chat-Badge nicht.
      // Der Firmenwechsel ist derselbe Fall: Gmail-Konten und Chat-Sicht
      // hängen an der Company, der Store muss neu aufsetzen.
      watch(
        () => [authStore.isAuthenticated, tablesStore.config.ActiveCompany as string] as const,
        ([authed, companyId], prev) => {
          if (!authed || !companyId) {
            if (!authed) communicationStore.stopPolling()
            return
          }
          const prevCompany = prev?.[1]
          if (communicationStore.initialized && prevCompany && prevCompany !== companyId) {
            communicationStore.restartPolling(companyId)
          } else {
            communicationStore.startPolling(companyId)
          }
          // Wire error reporting to the active company
          import('@/core/services/ErrorLogService').then(m => m.default.setCompanyId(companyId))
        },
        { immediate: true }
      )

      // Datenschutz-Zustimmung: Der Stand kommt zwar schon mit der Login- bzw.
      // Renew-Antwort, aber nicht jede Sitzung entsteht über diesen Weg — eine
      // aus IndexedDB wiederhergestellte Anmeldung durchläuft setAuth() nicht
      // mit einer frischen Serverantwort. Deshalb hier einmal je Anmeldung
      // nachfragen. Ein Fehler sperrt nicht (siehe refreshConsents()).
      watch(
        () => authStore.isAuthenticated,
        (authed) => { if (authed) authStore.refreshConsents() },
        { immediate: true }
      )
    });
    onUnmounted(() => {
      // No-op: listeners are managed elsewhere
    });
  },
});
</script>

<style lang="scss">
@import "bootstrap-icons/font/bootstrap-icons.css";
@import "apexcharts/dist/apexcharts.css";
@import "@fortawesome/fontawesome-free/css/all.min.css";
@import "socicon/css/socicon.css";
@import "line-awesome/dist/line-awesome/css/line-awesome.css";
@import "@vueform/multiselect/themes/default.css";
@import "v-calendar/style.css";
// Base styles of SweetAlert2. MUST stay: the JS build we import
// ("sweetalert2/dist/sweetalert2.js") ships no styles of its own, and without
// them .swal2-container has no `position: fixed` — every dialog then renders as
// plain content at the end of <body>, i.e. below the main area instead of over
// it. Has to come before the theme so metronic's overrides in
// assets/sass/vendors/plugins/_sweetalert2.scss still win.
@import "sweetalert2/dist/sweetalert2.css";

// Main theme style scss
@import "assets/fonticon/fonticon.css";
@import "assets/keenicons/duotone/style.css";
@import "assets/keenicons/outline/style.css";
@import "assets/keenicons/solid/style.css";
@import "assets/sass/plugins";
@import "assets/sass/style";
@import "assets/css/custom";

// Removed (verified unused): bootstrap-vue-3, quill, animate.css,
// nouislider, dropzone, prism-themes, element-plus


#app {
  display: contents;
}

html, body {
  overscroll-behavior-x: none;
}
</style>
